import {Dialog, Disclosure, Popover} from '@headlessui/react'
import classNames from 'classnames'
import {gsap} from 'gsap'
import {ScrollTrigger} from 'gsap/ScrollTrigger'
import * as React from 'react'
import {usePopper} from 'react-popper'

import Logo from '@/assets/Logo.tsx'
import IconClose from '@/assets/specify/IconClose'
import IconMenuClose from '@/assets/specify/IconMenuClose'
import {makeEditable} from '@/common/utils'
import Button from '@/components/Button'
import Buttons from '@/components/Buttons'
import {Grid} from '@/components/Grid'
import Image from '@/components/Image'
import Link from '@/components/Link'

export const TOP_NAVIGATION_HEIGHT = '32px'
export const NAVIGATION_HEIGHT = '80px'

const colors = {
  'purple-400': 'bg-purple-400 text-white',
  'turquoise-400': 'bg-turquoise-400',
  'violet-200': 'bg-violet-200',
  'violet-400': 'bg-violet-400',
  'violet-600': 'bg-violet-600 text-white',
  'yellow-400': 'bg-yellow-400',
}
const iconColors = {
  'turquoise-400': 'bg-turquoise-300 text-turquoise-600',
  'violet-400': 'bg-violet-400 text-white',
  'violet-200': 'bg-violet-300 text-white',
}
const highlightedIconColors = {
  'purple-400': 'bg-purple-400',
  'turquoise-400': 'bg-turquoise-600 text-turquoise-300',
  'violet-200': 'bg-violet-400',
  'violet-400': 'bg-violet-400',
  'violet-600': 'bg-violet-600',
  'yellow-400': 'bg-yellow-400',
}
const borderColors = {
  'purple-400': 'hover:border-purple-400 focus:border-purple-400',
  'turquoise-400': 'hover:border-turquoise-600 focus:border-turquoise-600',
  'violet-400': 'hover:border-violet-600 focus:border-violet-600',
  'violet-600': 'hover:border-violet-600 focus:border-violet-600',
  'yellow-400': 'hover:border-yellow-900 focus:border-yellow-900',
}
const highlightedColors = {
  'purple-400': 'bg-purple-200',
  'turquoise-400': 'bg-turquoise-300',
  'violet-200': 'bg-violet-300',
  'violet-400': 'bg-violet-300',
  'violet-600': 'bg-violet-200',
  'yellow-400': 'bg-yellow-200',
}
const backgroundColors = {
  'purple-400': 'bg-purple-200',
  'turquoise-400': 'bg-turquoise-200',
  'violet-200': 'bg-white',
  'violet-400': 'bg-violet-300',
  'violet-600': 'bg-violet-200',
  'yellow-400': 'bg-yellow-200',
}

const Caption = (props) => (
  <Link link={props.link} className='group flex w-fit flex-col gap-1 px-14'>
    <span
      className={classNames(
        'misc-button-s w-fit rounded-lg px-4 py-1 uppercase',
        colors[props.color]
      )}
    >
      {props.heading}
    </span>
    {props.image && (
      <div className='aspect-h-5 aspect-w-9 my-6 h-32 w-45 overflow-hidden rounded-lg '>
        <Image
          {...props.image}
          className='transition-scale duration-200 group-hover:scale-110'
          ratio='9:5'
        />
      </div>
    )}
    <div className='flex h-full w-45 flex-col gap-2'>
      <span className='body-s-medium text-purple-900'>{props.label}</span>
      {!!props.description && (
        <p className='body-xs-regular block h-full w-45 break-words text-purple-400'>
          {props.description}
        </p>
      )}
    </div>
  </Link>
)

const Item = ({link, icon, color, label, highlighted, description}) => (
  <Link
    link={link}
    className={classNames(
      'flex w-full flex-shrink items-center gap-1 rounded-2xl border border-white border-opacity-0 px-2 py-3 hover:border-opacity-100 focus:border-opacity-100 lg:w-[320px]',
      highlighted && highlightedColors[color],
      borderColors[color]
    )}
  >
    <div
      className={classNames(
        'mr-3 flex h-9 w-9 items-center justify-center rounded-md p-2',
        highlighted ? highlightedIconColors[color] : iconColors[color]
      )}
    >
      {icon !== '' ? (
        <div
          className={classNames(
            'aspect-h-1 aspect-w-1 h-5 w-5',
            highlighted ? highlightedIconColors[color] : iconColors[color]
          )}
        >
          <Image {...icon} ratio='1:1' sizes={{sm: '40', unit: 'px'}} fitIn />
        </div>
      ) : null}
    </div>
    <span className='flex flex-col justify-center'>
      <span className='body-s-medium block text-purple-900'>{label}</span>
      {!!description && (
        <span className='body-xs-regular line-clamp-1 block w-60 text-purple-400'>
          {description}
        </span>
      )}
    </span>
  </Link>
)

const Navigation = ({_editable, logo, logoLink, loginLabel, loginLink, items, buttons, story}) => {
  const navRef = React.useRef()
  const backgroundRef = React.useRef()
  const navContainerRef = React.useRef()

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const popoverButtonsRef = React.useRef([])

  let [referenceElement, setReferenceElement] = React.useState()
  let [popperElement, setPopperElement] = React.useState()
  const [arrowElement, setArrowElement] = React.useState()
  const [backgroundOpacity, setBackgroundOpacity] = React.useState(false)

  let {styles, attributes} = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
          offset: [0, 8],
        },
      },
    ],
  })

  const handleEnter = (isOpen, index) => {
    const button = popoverButtonsRef.current && popoverButtonsRef.current[index]
    if (!isOpen && button) button.click()
  }

  const handleLeave = (isOpen, index) => {
    const button = popoverButtonsRef.current && popoverButtonsRef.current[index]
    if (isOpen && button) button.click()
  }

  const handleOpen = () => {
    setMobileMenuOpen(true)
    ScrollTrigger.normalizeScroll(false)
  }

  const handleClose = () => {
    setMobileMenuOpen(false)
    ScrollTrigger.normalizeScroll(true)
  }

  React.useEffect(() => {
    const nav = navRef.current
    const background = backgroundRef.current

    if (nav && background) {
      const context = gsap.context(() => {
        ScrollTrigger.create({
          trigger: nav,
          pin: nav,
          pinSpacing: false,
          start: 'top 8px',
          end: 'max',
          onEnter: () => {
            setBackgroundOpacity(true)
          },
          onLeaveBack: () => {
            setBackgroundOpacity(false)
          },
        })
      })

      return () => context.revert()
    }
  }, [])

  const sectionRef = React.useRef(null)
  const [, setIsScrolling] = React.useState(false)

  React.useEffect(() => {
    const tweens = []
    const context = gsap.context(() => {
      tweens.push(
        gsap
          .from(navContainerRef.current, {
            yPercent: -110,
            duration: 0.25,
          })
          .progress(1)
      )
      if (sectionRef.current !== null) {
        tweens.push(
          gsap.set(sectionRef.current, {
            top: 0,
          })
        )
        tweens.push(
          gsap.to(sectionRef.current, {
            scrollTrigger: {
              start: 'top+=150 top',
              end: 'bottom',
              scrub: true,
              onUpdate: (self) => {
                self.direction === -1 ? tweens[0].play() : tweens[0].reverse()
              },
            },
          })
        )
      }
    })
    return () => context.revert()
  }, [])

  const onScroll = () => {
    if (window.scrollY > 88) {
      setIsScrolling(true)
    } else setIsScrolling(false)
  }

  React.useEffect(() => {
    const context = gsap.context(() => {
      window.addEventListener('scroll', onScroll)
      return () => {
        window.removeEventListener('scroll', onScroll)
      }
    })
    return () => context.revert()
  }, [])

  return (
    <section
      className='absolute left-0 top-0 z-10 w-full'
      {...makeEditable(_editable)}
      ref={sectionRef}
    >
      <Grid>
        <div
          className='body-xs-bold col-span-full flex items-center justify-end gap-1 overflow-hidden py-1 text-white'
          style={{height: TOP_NAVIGATION_HEIGHT}}
        >
          <Link link={loginLink}>{loginLabel}</Link>
          <LoginIcon />
        </div>
      </Grid>
      <div style={{height: NAVIGATION_HEIGHT}}>
        <Grid className='relative'>
          <div ref={navRef} className='relative col-span-full flex items-center gap-1'>
            <div ref={navContainerRef} className='flex w-full items-center py-4'>
              <div
                ref={backgroundRef}
                className={classNames(
                  'transition-200 absolute -left-3 -right-3 block h-full rounded-[theme(spacing.10)] bg-white bg-opacity-60 backdrop-blur duration-150 md:-left-8 md:-right-8 lg:-left-10 lg:-right-10',
                  {
                    'opacity-100': backgroundOpacity,
                    'opacity-0': !backgroundOpacity,
                  }
                )}
              />
              <Link
                className='relative mr-12 flex-none text-purple-900 xl:text-left'
                link={logoLink}
              >
                <Image {...logo} ratio='9:2' sizes={{sm: '40', unit: 'px'}} />
              </Link>
              <nav className='relative flex-1 overflow-visible whitespace-nowrap lg:-ml-2'>
                <div className='hidden items-center justify-between lg:flex'>
                  <div className='flex items-center'>
                    {items.map(
                      ({_uid, color, label, link, highlightedItems = [], items = []}, index) => {
                        const currentLinkItems = items.map((item) =>
                          item?.items?.find((el) =>
                            !el.link.story
                              ? el.link?.cached_url === story?.full_slug
                              : el.link.story && el.link?.story.full_slug === story?.full_slug
                          )
                        )

                        const currentLinkHighlighted = highlightedItems.find((el) =>
                          !el.link.story
                            ? el.link?.cached_url === story?.full_slug
                            : el.link.story && el.link?.story.full_slug === story?.full_slug
                        )

                        if (link && link.id) {
                          return (
                            <div
                              key={_uid}
                              className='group flex h-8 flex-col overflow-hidden px-2'
                            >
                              <Link
                                link={link}
                                activeClassName='body-s-medium translate-y-0 rounded-[40px] bg-violet-600 px-3 py-1 text-white'
                                className='body-s-medium rounded-[40px] px-3 py-1'
                              >
                                {label}
                              </Link>
                              <Link
                                link={link}
                                activeClassName='hidden'
                                className='body-s-medium translate-y-0 rounded-[40px] bg-violet-600 px-3 py-1 text-white transition-all duration-300 ease-out group-hover:-translate-y-8'
                              >
                                {label}
                              </Link>
                            </div>
                          )
                        } else if (Array.isArray(items)) {
                          return (
                            <Popover
                              key={_uid + '_popover'}
                              as={React.Fragment}
                              className='relative'
                              unmount='false'
                            >
                              {({open}) => (
                                <div
                                  onMouseEnter={() => handleEnter(open, index)}
                                  onMouseLeave={() => handleLeave(open, index)}
                                >
                                  <Popover.Button
                                    onClick={(e) => setReferenceElement(e.currentTarget)}
                                    ref={(e) => (popoverButtonsRef.current[index] = e)}
                                    className='h-full hover:outline-none hover:outline-0'
                                  >
                                    <div className='nav-container group flex h-8 flex-col overflow-hidden px-2'>
                                      <span className='body-s-medium rounded-[40px] px-3 py-1'>
                                        {label}
                                      </span>
                                      <span
                                        className={classNames({
                                          'body-s-medium -translate-y-8 rounded-[40px] bg-violet-600 px-3 py-1 text-white transition-all duration-300 ease-out':
                                            open || currentLinkItems[0] || currentLinkHighlighted,
                                        })}
                                      >
                                        {label}
                                      </span>
                                    </div>
                                  </Popover.Button>
                                  <Popover.Panel
                                    ref={setPopperElement}
                                    style={styles.popper}
                                    {...attributes.popper}
                                    className='mx-auto pt-4'
                                  >
                                    <div
                                      ref={setArrowElement}
                                      style={styles.arrow}
                                      className='z-10 before:visible before:absolute before:top-3 before:z-10 before:h-5 before:w-5 before:rotate-45 before:rounded
                                    before:bg-white before:shadow-[0px_0px_20px_rgba(40,12,45,0.2)] before:backdrop-blur'
                                    />
                                    <div className='relative z-20 ml-25 mt-5 flex flex-col whitespace-normal rounded-[40px] bg-white bg-opacity-80 py-10 shadow-[20px_10px_72px_rgba(40,12,45,0.2)] backdrop-blur'>
                                      <div className='flex w-fit'>
                                        <div
                                          className={classNames('flex gap-x-6', {
                                            'px-14': items.length > 0,
                                          })}
                                        >
                                          {items.map(({_uid, heading, items}) => {
                                            return (
                                              <div key={_uid}>
                                                {heading && (
                                                  <span
                                                    className={classNames(
                                                      'misc-button-s rounded-lg px-4 py-2 uppercase',
                                                      colors[color]
                                                    )}
                                                  >
                                                    {heading}
                                                  </span>
                                                )}
                                                <div className='relative z-40 mt-5 grid w-max grid-cols-2 gap-2'>
                                                  {items?.map((item) => (
                                                    <Item key={item._uid} {...item} color={color} />
                                                  ))}
                                                </div>
                                              </div>
                                            )
                                          })}
                                        </div>
                                        {highlightedItems.map((item, index) => {
                                          return (
                                            <div
                                              key={item._uid}
                                              className='relative overflow-hidden'
                                            >
                                              <div
                                                key={item._uid}
                                                className={classNames(
                                                  'absolute top-11 h-66 w-[1px] border-l border-purple-300',
                                                  {
                                                    'border-none':
                                                      highlightedItems.length > 1 && index === 0,
                                                  }
                                                )}
                                              />
                                              <Caption {...item} />
                                            </div>
                                          )
                                        })}
                                      </div>
                                    </div>
                                  </Popover.Panel>
                                </div>
                              )}
                            </Popover>
                          )
                        }
                        return null
                      }
                    )}
                  </div>
                  <Buttons
                    buttons={buttons}
                    buttonProps={{hasIcon: false, size: 'small'}}
                    className='flex gap-2'
                  />
                </div>
                <div className='flex justify-end lg:hidden'>
                  <button
                    type='button'
                    className='h-10 w-10 rounded-full bg-violet-600 p-2'
                    onClick={handleOpen}
                  >
                    <span className='sr-only'>Open main menu</span>
                    <span aria-hidden='true' className='text-white'>
                      <IconMenuClose />
                    </span>
                  </button>
                </div>
              </nav>
              <Dialog
                as='div'
                className='lg:hidden'
                open={mobileMenuOpen}
                onClose={setMobileMenuOpen}
              >
                <div className='fixed inset-0 z-10' />
                <Dialog.Panel className='fixed inset-y-0 z-10 w-full overflow-y-auto bg-violet-600'>
                  <div className='flex items-center justify-between bg-white p-4'>
                    <Link link={logoLink}>
                      <Logo aria-hidden />
                    </Link>
                    <button
                      type='button'
                      className='h-10 w-10 rounded-full bg-violet-600 p-2'
                      onClick={handleClose}
                    >
                      <span className='sr-only'>Close menu</span>
                      <span aria-hidden='true' className='text-white'>
                        <IconClose />
                      </span>
                    </button>
                  </div>
                  <div className='mt-12 flow-root p-4'>
                    <div className='flex flex-col items-center gap-10'>
                      {items.map(
                        (
                          {_uid, color, label, link, highlightedItems = [], items = []},
                          i,
                          array
                        ) => {
                          if (link && link.id) {
                            const isLastItem = i === array.length - 1
                            if (isLastItem) {
                              return (
                                <Button
                                  key={_uid}
                                  link={link}
                                  className='mt-auto justify-self-end'
                                  hasIcon={false}
                                  fullWidth
                                >
                                  {label}
                                </Button>
                              )
                            }
                            return (
                              <div key={_uid}>
                                <Link
                                  link={link}
                                  className='button-m-medium flex w-full items-center justify-between text-white'
                                  onClick={handleClose}
                                >
                                  {label}
                                </Link>
                              </div>
                            )
                          }
                          return (
                            <Disclosure key={_uid} as='div' className='w-full'>
                              {({open}) => (
                                <>
                                  <Disclosure.Button
                                    className={classNames(
                                      'button-s-medium flex w-full items-center justify-center rounded-t-2xl py-2',
                                      {[`text-purple-900 ${colors[color]}`]: open},
                                      {'text-white': !open}
                                    )}
                                  >
                                    <span>{label}</span>
                                  </Disclosure.Button>
                                  <Disclosure.Panel
                                    className={classNames(
                                      'flex w-full flex-col gap-4 rounded-b-2xl p-6',
                                      backgroundColors[color]
                                    )}
                                  >
                                    {items.map(({_uid, items}) => {
                                      return (
                                        <div key={_uid}>
                                          {items?.map((item) => (
                                            <Item key={item._uid} {...item} color={color} />
                                          ))}
                                        </div>
                                      )
                                    })}
                                    {!!highlightedItems.length > 0 &&
                                      highlightedItems.map((item) => {
                                        return (
                                          <div
                                            key={item._uid}
                                            className='flex w-full justify-center'
                                          >
                                            <Item {...item} highlighted />
                                          </div>
                                        )
                                      })}
                                  </Disclosure.Panel>
                                </>
                              )}
                            </Disclosure>
                          )
                        }
                      )}
                    </div>
                    <Buttons
                      buttons={buttons}
                      buttonProps={{hasIcon: false, size: 'small'}}
                      className='mt-20 flex flex-col items-center gap-4'
                    />
                  </div>
                </Dialog.Panel>
              </Dialog>
            </div>
          </div>
        </Grid>
      </div>
    </section>
  )
}

const LoginIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <circle cx='12' cy='12' r='10' fill='#BEB4FF' />
    <circle cx='12' cy='10' r='3' fill='white' />
    <path
      d='M5.44141 19.5488C6.43484 16.8918 8.99662 15 12.0002 15C15.0039 15 17.5656 16.8918 18.5591 19.5488C16.8033 21.0756 14.5097 22 12.0002 22C9.49076 22 7.19718 21.0756 5.44141 19.5488Z'
      fill='white'
    />
  </svg>
)

export default Navigation
